import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, Wrench, Search, CheckCircle2, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  updateCleaningRecord, createManualCleaning, updateManualCleaning, deleteManualCleaning,
  getTenant,
} from '@/lib/api';
import { todayIso } from '@/lib/dateRange';
import { formatShortDate } from './housekeepingUtils';
import { CleaningEditDialog } from './CleaningEditDialog';
import { ManualCleaningDialog } from './ManualCleaningDialog';

// Status helper — classifies a cleaning row into one of three visual states.
// Done     → user marked confirmed; row gets a soft green highlight.
// Overdue  → checkout has already passed and nobody has confirmed yet.
// Pending  → upcoming / not yet past checkout, awaiting confirmation.
function cleaningStatus(record, todayIso) {
  if (record.confirmed) return 'done';
  const co = record.check_out_date || '';
  if (co && co < todayIso) return 'overdue';
  return 'pending';
}

const STATUS_STYLES = {
  done:    { badge: 'bg-emerald-100 text-emerald-800 border-emerald-300', label: 'Done' },
  overdue: { badge: 'bg-rose-100 text-rose-800 border-rose-300', label: 'Overdue' },
  pending: { badge: 'bg-amber-50 text-amber-800 border-amber-200', label: 'Pending' },
};

export function UpcomingCleaningsTab({ records, manualCleanings, housekeepers, maintenancePersonnel, unitMap, propMap, units, doorCodeMap = {}, onRefresh, setSelectedTenantForPopup }) {
  const [editModal, setEditModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [isEditingManual, setIsEditingManual] = useState(false);

  const [createModal, setCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    unit_id: '', tenant_name: '', check_out_date: '', next_check_in_date: '',
    check_out_time: '', check_in_time: '', cleaning_date: '', cleaning_time: '',
    assigned_cleaner_id: null, assigned_cleaner_name: '',
    assigned_maintenance_id: null, assigned_maintenance_name: '',
    maintenance_note: '', notes: '', confirmed: false,
  });
  const [creating, setCreating] = useState(false);

  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | pending | overdue | done

  const today = todayIso();

  // Wrap onRefresh so save/create/delete handlers keep the active range.
  const refreshWithFilters = () => {
    if (filterFrom || filterTo) {
      onRefresh({ from_date: filterFrom || undefined, to_date: filterTo || undefined });
    } else {
      onRefresh();
    }
  };

  // Fetch past records when admins pick an explicit date range. We only
  // call onRefresh when the filters actually change — the mount fetch is
  // already handled by HousekeepingPage, so skip it here to avoid a
  // double round-trip.
  const [initialSkip, setInitialSkip] = useState(true);
  useEffect(() => {
    if (initialSkip) { setInitialSkip(false); return; }
    refreshWithFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterFrom, filterTo]);

  const mergedRecords = [
    ...records.map(r => ({ ...r, _isManual: false })),
    ...manualCleanings.map(mc => ({ ...mc, _isManual: true, check_out_date: mc.check_out_date || '' })),
  ].sort((a, b) => (a.check_out_date || '').localeCompare(b.check_out_date || ''));

  const filteredRecords = mergedRecords.filter(r => {
    if (filterFrom && r.check_out_date && r.check_out_date < filterFrom) return false;
    if (filterTo && r.check_out_date && r.check_out_date > filterTo) return false;
    if (statusFilter !== 'all' && cleaningStatus(r, today) !== statusFilter) return false;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const u = unitMap[r.unit_id];
      const propName = propMap[u?.property_id]?.name || '';
      const text = `${propName} ${u?.unit_number || ''} ${r.tenant_name || ''} ${r.assigned_cleaner_name || ''} ${r.notes || ''}`.toLowerCase();
      if (!text.includes(term)) return false;
    }
    return true;
  });

  const openEdit = (r) => {
    setSelectedRecord(r);
    setIsEditingManual(!!r._isManual);
    setEditForm({
      unit_id: r.unit_id || '',
      tenant_name: r.tenant_name || '',
      check_out_date: r.check_out_date || '',
      next_check_in_date: r.next_check_in_date || '',
      check_out_time: r.check_out_time || '',
      check_in_time: r.check_in_time || '',
      cleaning_date: r.cleaning_date || '',
      cleaning_time: r.cleaning_time || '',
      assigned_cleaner_id: r.assigned_cleaner_id || null,
      assigned_cleaner_name: r.assigned_cleaner_name || '',
      confirmed: r.confirmed || false,
      notes: r.notes || '',
      assigned_maintenance_id: r.assigned_maintenance_id || null,
      assigned_maintenance_name: r.assigned_maintenance_name || '',
      maintenance_note: r.maintenance_note || '',
    });
    setEditModal(true);
  };

  const handleSave = async () => {
    if (!selectedRecord) return;
    setSaving(true);
    try {
      const payload = { ...editForm };
      if (payload.assigned_cleaner_id) {
        payload.assigned_cleaner_name = housekeepers.find(h => h.id === payload.assigned_cleaner_id)?.name || '';
      } else {
        payload.assigned_cleaner_id = null;
        payload.assigned_cleaner_name = '';
      }
      if (isEditingManual) {
        const u = unitMap[payload.unit_id];
        payload.unit_label = u ? u.unit_number : '';
        await updateManualCleaning(selectedRecord.id, payload);
      } else {
        await updateCleaningRecord(selectedRecord.id, payload);
      }
      setEditModal(false);
      refreshWithFilters();
      toast.success('Updated');
    } catch {
      toast.error('Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    if (!createForm.unit_id) { toast.error('Please select a unit'); return; }
    if (!createForm.check_out_date) { toast.error('Please select a date'); return; }
    setCreating(true);
    try {
      const payload = { ...createForm };
      if (payload.assigned_cleaner_id) {
        payload.assigned_cleaner_name = housekeepers.find(h => h.id === payload.assigned_cleaner_id)?.name || '';
      } else {
        payload.assigned_cleaner_id = null;
        payload.assigned_cleaner_name = '';
      }
      const u = unitMap[payload.unit_id];
      payload.unit_label = u ? u.unit_number : '';
      await createManualCleaning(payload);
      setCreateModal(false);
      setCreateForm({
        unit_id: '', tenant_name: '', check_out_date: '', next_check_in_date: '',
        check_out_time: '', check_in_time: '', cleaning_date: '', cleaning_time: '',
        assigned_cleaner_id: null, assigned_cleaner_name: '',
        assigned_maintenance_id: null, assigned_maintenance_name: '',
        maintenance_note: '', notes: '', confirmed: false,
      });
      refreshWithFilters();
      toast.success('Manual cleaning created');
    } catch {
      toast.error('Failed to create');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteManual = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Delete this manual cleaning?')) return;
    try { await deleteManualCleaning(id); refreshWithFilters(); toast.success('Deleted'); }
    catch { toast.error('Failed to delete'); }
  };

  const anyMaint = filteredRecords.some(r => r.assigned_maintenance_name);

  // ─── Bulk selection + bulk edits ─────────────────────────────
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkCleanerId, setBulkCleanerId] = useState('_keep');
  const [bulkDate, setBulkDate] = useState('');
  const [bulkTime, setBulkTime] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);

  // Dropping filters can hide selected rows — keep the selection honest.
  useEffect(() => { setSelectedIds([]); }, [filterFrom, filterTo, searchTerm, statusFilter]);

  const selectedRecords = filteredRecords.filter(r => selectedIds.includes(r.id));
  const allSelected = filteredRecords.length > 0 && selectedRecords.length === filteredRecords.length;
  const toggleRow = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleAll = () => setSelectedIds(allSelected ? [] : filteredRecords.map(r => r.id));
  const resetBulk = () => { setSelectedIds([]); setBulkCleanerId('_keep'); setBulkDate(''); setBulkTime(''); };

  // Rebuild the record's full payload (both PUT endpoints replace the doc)
  // and apply only the fields the user actually changed.
  const buildPayload = (r, overrides) => {
    const base = {
      check_in_time: r.check_in_time || '',
      check_out_time: r.check_out_time || '',
      cleaning_date: r.cleaning_date || '',
      cleaning_time: r.cleaning_time || '',
      assigned_cleaner_id: r.assigned_cleaner_id || null,
      assigned_cleaner_name: r.assigned_cleaner_name || '',
      confirmed: !!r.confirmed,
      notes: r.notes || '',
      assigned_maintenance_id: r.assigned_maintenance_id || null,
      assigned_maintenance_name: r.assigned_maintenance_name || '',
      maintenance_note: r.maintenance_note || '',
    };
    if (r._isManual) {
      Object.assign(base, {
        unit_id: r.unit_id || '',
        unit_label: unitMap[r.unit_id]?.unit_number || r.unit_label || '',
        tenant_name: r.tenant_name || '',
        check_out_date: r.check_out_date || '',
        next_check_in_date: r.next_check_in_date || '',
      });
    }
    return { ...base, ...overrides };
  };

  const applyToSelected = async (overrides, successMsg) => {
    setBulkSaving(true);
    try {
      const results = await Promise.allSettled(selectedRecords.map(r => {
        const payload = buildPayload(r, typeof overrides === 'function' ? overrides(r) : overrides);
        return r._isManual ? updateManualCleaning(r.id, payload) : updateCleaningRecord(r.id, payload);
      }));
      const failed = results.filter(x => x.status === 'rejected').length;
      resetBulk();
      refreshWithFilters();
      if (failed) toast.error(`${failed} of ${results.length} cleanings failed to update`);
      else toast.success(successMsg);
    } finally {
      setBulkSaving(false);
    }
  };

  const handleBulkApply = () => {
    const overrides = {};
    const changes = [];
    if (bulkCleanerId !== '_keep') {
      const id = bulkCleanerId === '_none' ? null : bulkCleanerId;
      overrides.assigned_cleaner_id = id;
      overrides.assigned_cleaner_name = id ? (housekeepers.find(h => h.id === id)?.name || '') : '';
      changes.push(`cleaner → ${id ? overrides.assigned_cleaner_name : 'Unassigned'}`);
    }
    if (bulkDate) { overrides.cleaning_date = bulkDate; changes.push(`cleaning date → ${bulkDate}`); }
    if (bulkTime) { overrides.cleaning_time = bulkTime; changes.push(`cleaning time → ${bulkTime}`); }
    if (changes.length === 0) { toast.error('Pick a cleaner, date, or time to apply'); return; }
    if (!window.confirm(`Apply to ${selectedRecords.length} cleaning(s)?\n\n${changes.join('\n')}\n\nAll other details stay unchanged.`)) return;
    applyToSelected(overrides, `Updated ${selectedRecords.length} cleaning(s)`);
  };

  const handleBulkMarkDone = () => {
    const open = selectedRecords.filter(r => !r.confirmed);
    if (open.length === 0) { toast.error('Selected cleanings are already done'); return; }
    if (!window.confirm(`Mark ${open.length} cleaning(s) as done? Records are kept, nothing is deleted.`)) return;
    applyToSelected({ confirmed: true }, `Marked ${open.length} cleaning(s) done`);
  };


  return (
    <div>
      <div className="flex items-end gap-3 mb-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <p className="text-xs text-muted-foreground mb-2">
            Upcoming checkouts in the next 60 days plus any overdue cleanings. Pick a date range to browse past cleanings. Click any row to edit details.
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5">
              <Input type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)} className="h-8 w-36 text-xs" data-testid="cleaning-filter-from" />
              <span className="text-xs text-muted-foreground">to</span>
              <Input type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)} className="h-8 w-36 text-xs" data-testid="cleaning-filter-to" />
            </div>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="h-8 w-52 text-xs pl-8" placeholder="Search unit, cleaner, notes..." data-testid="cleaning-search-input" />
            </div>
            <div className="flex items-center gap-1" data-testid="cleaning-status-filter">
              {['all', 'pending', 'overdue', 'done'].map(key => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setStatusFilter(key)}
                  className={`text-xs px-2 py-1 rounded border transition-colors ${
                    statusFilter === key
                      ? (key === 'done'    ? 'bg-emerald-600 text-white border-emerald-600'
                       : key === 'overdue' ? 'bg-rose-600 text-white border-rose-600'
                       : key === 'pending' ? 'bg-amber-500 text-white border-amber-500'
                       : 'bg-slate-800 text-white border-slate-800')
                      : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                  }`}
                  data-testid={`cleaning-status-filter-${key}`}
                >
                  {key === 'all' ? 'All' : STATUS_STYLES[key].label}
                </button>
              ))}
            </div>
            {(filterFrom || filterTo || searchTerm || statusFilter !== 'all') && (
              <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => { setFilterFrom(''); setFilterTo(''); setSearchTerm(''); setStatusFilter('all'); }}>
                Clear
              </Button>
            )}
          </div>
        </div>
        <Button size="sm" onClick={() => setCreateModal(true)} data-testid="add-manual-cleaning-btn">
          <Plus className="h-4 w-4 mr-1" />Add Manual Cleaning
        </Button>
      </div>

      {selectedRecords.length > 0 && (
        <div className="mb-3 rounded-lg border border-slate-300 bg-slate-50 px-3 py-2.5 flex flex-wrap items-end gap-3" data-testid="cleaning-bulk-bar">
          <span className="text-xs font-semibold text-slate-700 pb-2" data-testid="cleaning-bulk-count">
            {selectedRecords.length} selected
          </span>
          <div>
            <Label className="text-[11px] text-muted-foreground">Cleaner</Label>
            <Select value={bulkCleanerId} onValueChange={setBulkCleanerId}>
              <SelectTrigger className="h-8 w-44 text-xs mt-0.5" data-testid="cleaning-bulk-cleaner"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_keep">Keep current</SelectItem>
                <SelectItem value="_none">Unassigned</SelectItem>
                {housekeepers.map(h => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] text-muted-foreground">Cleaning date</Label>
            <Input type="date" value={bulkDate} onChange={e => setBulkDate(e.target.value)} className="h-8 w-36 text-xs mt-0.5" data-testid="cleaning-bulk-date" />
          </div>
          <div>
            <Label className="text-[11px] text-muted-foreground">Cleaning time</Label>
            <Input type="time" value={bulkTime} onChange={e => setBulkTime(e.target.value)} className="h-8 w-28 text-xs mt-0.5" data-testid="cleaning-bulk-time" />
          </div>
          <Button size="sm" className="h-8 text-xs" disabled={bulkSaving} onClick={handleBulkApply} data-testid="cleaning-bulk-apply">
            {bulkSaving ? 'Saving...' : 'Apply to selected'}
          </Button>
          <Button size="sm" variant="outline" className="h-8 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50" disabled={bulkSaving} onClick={handleBulkMarkDone} data-testid="cleaning-bulk-mark-done">
            <CheckCircle2 className="h-3.5 w-3.5 mr-1" />Mark done
          </Button>
          <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={resetBulk} data-testid="cleaning-bulk-clear">
            <X className="h-3.5 w-3.5 mr-1" />Clear
          </Button>
        </div>
      )}

      {filteredRecords.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm border rounded bg-muted/20">
          {mergedRecords.length === 0 ? 'No cleanings found' : 'No cleanings match your filters'}
        </div>
      ) : (
        <div className="rounded border border-border/60 overflow-x-auto bg-white" data-testid="upcoming-cleanings-list">
          <table className="w-full text-sm" data-testid="upcoming-cleanings-table">
            <thead>
              <tr className="bg-[#f5f0eb] text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground border-b border-border/50">
                <th className="px-3 py-2.5 w-9">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={toggleAll}
                    aria-label="Select all cleanings"
                    data-testid="cleaning-select-all"
                  />
                </th>
                <th className="px-3 py-2.5">Unit</th>
                <th className="px-3 py-2.5">Check-out</th>
                <th className="px-3 py-2.5">Cleaning Date</th>
                <th className="px-3 py-2.5">Check-in</th>
                <th className="px-3 py-2.5">Cleaning Time</th>
                <th className="px-3 py-2.5">Cleaner</th>
                {anyMaint && <th className="px-3 py-2.5">Maintenance</th>}
                <th className="px-3 py-2.5">Notes</th>
                <th className="px-3 py-2.5">Door Code</th>
                <th className="px-3 py-2.5 text-right">Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecords.map((r, idx) => {
                const u = unitMap[r.unit_id];
                const isManual = r._isManual;
                const status = cleaningStatus(r, today);
                // Done rows get a subtle green background so finished
                // cleanings pop. Overdue rows adopt a rose tint so a late
                // cleaning is impossible to miss. Manual (unlinked) rows
                // keep their red tint only when pending/overdue — a Done
                // manual row still goes green.
                const rowBg = status === 'done'
                  ? 'bg-emerald-50'
                  : status === 'overdue'
                    ? 'bg-rose-50'
                    : isManual
                      ? 'bg-red-50'
                      : idx % 2 === 0 ? 'bg-white' : 'bg-[#fafaf7]';
                return (
                  <tr
                    key={r.id}
                    className={`${rowBg} ${selectedIds.includes(r.id) ? 'ring-1 ring-inset ring-slate-400' : ''} cursor-pointer hover:bg-[#f0ece6] transition-colors border-b border-border/30 last:border-b-0`}
                    onClick={() => openEdit(r)}
                    data-testid={isManual ? 'manual-cleaning-row' : 'cleaning-row'}
                    data-status={status}
                  >
                    <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedIds.includes(r.id)}
                        onCheckedChange={() => toggleRow(r.id)}
                        aria-label="Select cleaning"
                        data-testid="cleaning-row-checkbox"
                      />
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      {(() => {
                        const prop = propMap[u?.property_id];
                        const propName = prop?.name || '';
                        const unitNum = u?.unit_number || r.unit_label || '?';
                        return <span className="font-semibold tabular-nums">{propName ? `${propName} Apt ${unitNum}` : unitNum}</span>;
                      })()}
                      <span
                        className="text-muted-foreground ml-1.5 text-xs hover:text-primary hover:underline transition-colors"
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!r.tenant_id) {
                            // Manual cleanings have a label/name but no tenant link.
                            if (r.tenant_name) {
                              setSelectedTenantForPopup({
                                name: r.tenant_name,
                                unit_number: u?.unit_number,
                                property_name: propMap[u?.property_id]?.name,
                              });
                            }
                            return;
                          }
                          // Pull the live tenant doc so the popup shows phone,
                          // email, dates, rent, deposit, notes — same source
                          // of truth Move-In/Move-Out uses.
                          try {
                            const full = await getTenant(r.tenant_id);
                            setSelectedTenantForPopup({
                              ...full,
                              property_name: propMap[full.property_id]?.name || propMap[u?.property_id]?.name,
                              unit_number: unitMap[full.unit_id]?.unit_number || u?.unit_number,
                            });
                          } catch {
                            toast.error('Failed to load tenant info');
                          }
                        }}
                      >
                        {r.tenant_name || ''}
                      </span>
                      {isManual && (
                        <span className="ml-1.5 text-[10px] font-semibold uppercase tracking-wide text-red-600 bg-red-100 border border-red-200 px-1.5 py-0.5 rounded" data-testid="manual-label">Manual</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      {formatShortDate(r.check_out_date)}
                      {r.check_out_time && <span className="text-muted-foreground ml-1">{r.check_out_time}</span>}
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap" data-testid="cleaning-date-cell">
                      {r.cleaning_date ? formatShortDate(r.cleaning_date) : '\u2014'}
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      {r.next_check_in_date ? formatShortDate(r.next_check_in_date) : '\u2014'}
                      {r.check_in_time && <span className="text-muted-foreground ml-1">{r.check_in_time}</span>}
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap">{r.cleaning_time || '\u2014'}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap">{r.assigned_cleaner_name || '\u2014'}</td>
                    {anyMaint && (
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        {r.assigned_maintenance_name ? (
                          <span className="inline-flex items-center gap-1 text-amber-700">
                            <Wrench className="h-3 w-3" />
                            {r.assigned_maintenance_name}
                            {r.maintenance_note && <span className="text-amber-500 text-xs">{'\u2014'} {r.maintenance_note}</span>}
                          </span>
                        ) : '\u2014'}
                      </td>
                    )}
                    <td className="px-3 py-2.5 max-w-[200px] truncate text-muted-foreground">{r.notes || '\u2014'}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap font-mono tabular-nums" data-testid="cleaning-door-code-cell">
                      {doorCodeMap[r.unit_id]?.guest_code || '\u2014'}
                    </td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">
                      <div className="inline-flex items-center gap-1.5">
                        <span className={`text-xs px-2 py-0.5 rounded border ${STATUS_STYLES[status].badge}`} data-testid="cleaning-status-badge">
                          {STATUS_STYLES[status].label}
                        </span>
                        {isManual && (
                          <button
                            onClick={(e) => handleDeleteManual(e, r.id)}
                            className="p-1 rounded hover:bg-red-100 text-red-400 hover:text-red-600 transition-colors"
                            data-testid="delete-manual-cleaning-btn"
                            title="Delete manual cleaning"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <CleaningEditDialog
        open={editModal}
        onOpenChange={setEditModal}
        record={selectedRecord}
        isManual={isEditingManual}
        form={editForm}
        setForm={setEditForm}
        housekeepers={housekeepers}
        maintenancePersonnel={maintenancePersonnel}
        units={units}
        unitMap={unitMap}
        propMap={propMap}
        onSave={handleSave}
        saving={saving}
      />

      <ManualCleaningDialog
        open={createModal}
        onOpenChange={setCreateModal}
        form={createForm}
        setForm={setCreateForm}
        housekeepers={housekeepers}
        maintenancePersonnel={maintenancePersonnel}
        units={units}
        properties={propMap}
        onSave={handleCreate}
        saving={creating}
      />
    </div>
  );
}
